import React, { useMemo } from "react"
import { Box, Container, Heading, Text, Stack, StackDivider } from "@chakra-ui/react"
import { useImage } from "@app/hooks/useImage"
import Image from "@app/components/Image/Image"

type Product = {
  product?: any
}

type Annotation = {
  _key: string
  title: string
  text: string
  icon: any
}

const ProductDiagram = ({ product }: Product) => {
  const { getGatsbyImage } = useImage()

  const diagramAnnotations = useMemo(() => {
    return product?.diagramAnnotations?.map((annotation: Annotation) => ({ ...annotation, icon: getGatsbyImage(annotation?.icon) }))
  }, [getGatsbyImage, product?.diagramAnnotations])

  const desktopImage = useMemo(
    () => getGatsbyImage(product?.diagramImage || product?.diagramImageDesktop),
    [getGatsbyImage, product?.diagramImage, product?.diagramImageDesktop]
  )

  const mobileImage = useMemo(
    () => (product?.diagramImageMobile?.length ? getGatsbyImage(product?.diagramImageMobile) : null),
    [getGatsbyImage, product?.diagramImageMobile]
  )

  const shouldShow = useMemo(
    () => desktopImage || mobileImage || !!diagramAnnotations?.length,
    [desktopImage, diagramAnnotations?.length, mobileImage]
  )

  return (
    shouldShow && (
      <Container my={{ base: 1, md: 6 }}>
        <Stack direction={{ base: `column`, md: `row` }} gap={{ base: 4, md: `70px` }}>
          <Box width={{ base: `full`, md: `50%` }}>
            <Image
              {...desktopImage}
              display={mobileImage ? { base: `none`, md: `block` } : `block`}
              width={`100%`}
              height={`100%`}
              objectFit={`cover`}
              objectPosition={`center`}
              borderRadius={`15px`}
            />
            {mobileImage && <Image {...mobileImage} display={{ base: `block`, md: `none` }} width={`100%`} borderRadius={`15px`} />}
          </Box>
          <Box width={{ base: `full`, md: `50%` }}>
            <Heading as={`h2`} size={`h2`} mb={1} textAlign={`center`}>
              {product?.diagramTitle}
            </Heading>
            <Stack direction={`column`} gap={1} divider={<StackDivider borderColor="brand.divider" />}>
              {diagramAnnotations?.map(({ _key, title, text, icon }: Annotation) => (
                <Stack direction={`row`} key={_key} gap={1} alignItems={`center`}>
                  <Box minWidth={`95px`} minHeight={`95px`} display={`flex`} alignItems={`center`} justifyContent={`center`}>
                    <Image {...icon} width={`65px`} height={`65px`} objectFit={`contain`} />
                  </Box>
                  <Stack direction={`column`}>
                    {title && (
                      <Text size={`lg`} fontWeight={600}>
                        {title}
                      </Text>
                    )}
                    {text && <Text size={`sm`}>{text}</Text>}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Container>
    )
  )
}

export default React.memo(ProductDiagram)
